/*
  ZEVQILO Atelier — predictive-search.js
  Search-as-you-type using Shopify's /search/suggest endpoint rendered through the
  "predictive-search" section. Debounced, cancellable and keyboard accessible (combobox pattern).
*/
(() => {
  const theme = window.theme;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      this.form = this.querySelector('form');
      if (!this.input || !this.results) return;

      this.cache = new Map();
      this.onInput = theme.debounce(this.search.bind(this), 260);
      this.onKeydown = this.onKeydown.bind(this);
      this.onReset = this.onReset.bind(this);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);
      this.form?.addEventListener('reset', this.onReset);
    }

    disconnectedCallback() {
      this.input?.removeEventListener('input', this.onInput);
      this.input?.removeEventListener('keydown', this.onKeydown);
      this.form?.removeEventListener('reset', this.onReset);
      this.controller?.abort();
    }

    get term() {
      return this.input.value.trim();
    }

    onReset() {
      window.setTimeout(() => {
        this.clear();
        this.input.focus();
      });
    }

    clear() {
      this.controller?.abort();
      this.results.innerHTML = '';
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.toggleAttribute('data-has-term', false);
      this.querySelector('[data-predictive-default]')?.removeAttribute('hidden');
    }

    async search() {
      const term = this.term;
      if (!term) {
        this.clear();
        return;
      }
      this.toggleAttribute('data-has-term', true);
      this.querySelector('[data-predictive-default]')?.setAttribute('hidden', '');

      if (this.cache.has(term)) {
        this.render(this.cache.get(term));
        return;
      }

      this.controller?.abort();
      this.controller = new AbortController();
      this.setAttribute('aria-busy', 'true');

      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '4',
        'resources[limit_scope]': 'each',
        'resources[options][fields]': 'title,product_type,variants.title,tag,vendor',
        section_id: 'predictive-search',
      });

      try {
        const doc = await theme.fetchHTML(`${theme.routes.predictiveSearch}?${params}`, {
          signal: this.controller.signal,
        });
        const markup = doc.querySelector('[data-predictive-markup]')?.innerHTML || '';
        this.cache.set(term, markup);
        if (this.term === term) this.render(markup);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.results.hidden = false;
          this.results.textContent = theme.strings.networkError;
        }
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      const count = this.results.querySelector('[data-result-count]');
      if (this.status && count) this.status.textContent = count.dataset.resultCount;
    }

    options() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    onKeydown(event) {
      const options = this.options();
      if (!options.length) return;
      const current = options.findIndex((el) => el.getAttribute('aria-selected') === 'true');

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        const next = (current + delta + options.length) % options.length;
        options.forEach((el) => el.setAttribute('aria-selected', 'false'));
        options[next].setAttribute('aria-selected', 'true');
        options[next].scrollIntoView({ block: 'nearest' });
        this.input.setAttribute('aria-activedescendant', options[next].id);
      } else if (event.key === 'Enter' && current > -1) {
        const link = options[current].querySelector('a') || options[current].closest('a');
        if (link) {
          event.preventDefault();
          link.click();
        }
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
